import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@sho-bot/ui';
import type { ReactNode } from 'react';
import { COMPONENT_IDS } from '../../shared/status.js';
import type { RoutePath } from '../navigation.js';
import { MetricGrid } from './metric.js';
import { PageSection } from './page-section.js';
import { SettingsPage, SettingsSection } from './settings/settings-page.js';

function times(count: number): number[] {
  return Array.from({ length: count }, (_unused, index) => index);
}

function Busy({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div role="status" aria-busy="true" aria-live="polite" aria-label={label}>
      {children}
    </div>
  );
}

function TabChrome({ labels }: { labels: readonly string[] }): ReactNode {
  return (
    <div className="flex flex-wrap gap-1.5" aria-hidden="true">
      {labels.map((label, index) => (
        <span
          key={label}
          className={cn(
            'inline-flex h-7 items-center rounded-md px-2.5 text-sm font-medium',
            index === 0 ? 'bg-tint-secondary text-text-primary' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function FieldSkeleton({ controlClassName }: { controlClassName: string }): ReactNode {
  return (
    <div className="grid gap-2">
      <Skeleton className="h-3.5 w-28" />
      <Skeleton className={controlClassName} />
    </div>
  );
}

export function MetricGridSkeleton({
  count,
  className,
}: {
  count: number;
  className?: string;
}): ReactNode {
  return (
    <MetricGrid className={className ?? ''}>
      {times(count).map((index) => (
        <div key={index} className="flex flex-col gap-0.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-6 w-16" />
        </div>
      ))}
    </MetricGrid>
  );
}

export function TableSkeleton({ columns, rows }: { columns: number; rows: number }): ReactNode {
  const widths = ['w-28', 'w-20', 'w-24', 'w-16', 'w-32', 'w-20', 'w-16'] as const;

  return (
    <div className="table-scroll table-scroll--bounded" aria-hidden="true">
      <Table>
        <TableHeader>
          <TableRow>
            {times(columns).map((index) => (
              <TableHead key={index}>
                <Skeleton className={cn('h-3', widths[index % widths.length])} />
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {times(rows).map((row) => (
            <TableRow key={row}>
              {times(columns).map((index) => (
                <TableCell key={index}>
                  <Skeleton className={cn('h-4', widths[(index + row) % widths.length])} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function StatusListSkeleton(): ReactNode {
  return (
    <Busy label="Loading current status">
      <ul className="grid gap-x-6 md:grid-cols-2" aria-hidden="true">
        {COMPONENT_IDS.map((id) => (
          <li key={id} className="flex min-w-0 items-start justify-between gap-4 py-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-20" />
          </li>
        ))}
      </ul>
    </Busy>
  );
}

export function StatusHistorySkeleton(): ReactNode {
  return (
    <Busy label="Loading status history">
      <div className="flex flex-col gap-4" aria-hidden="true">
        <Skeleton className="h-10 w-full" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-3">
            {times(4).map((index) => (
              <Skeleton key={index} className="h-3 w-16" />
            ))}
          </div>
          <Skeleton className="h-7 w-28" />
        </div>
        <TableSkeleton columns={4} rows={3} />
      </div>
    </Busy>
  );
}

function SettingsRowSkeleton({ control }: { control: 'text' | 'switch' | 'select' }): ReactNode {
  const controlClass =
    control === 'switch' ? 'h-5 w-9 rounded-full' : control === 'select' ? 'h-7 w-44' : 'h-4 w-32';

  return (
    <div className="flex min-h-11 items-center gap-4 border-t border-border-secondary px-4 py-3 first:border-t-0">
      <Skeleton className="h-4 w-36" />
      <Skeleton className={cn('ml-auto shrink-0', controlClass)} />
    </div>
  );
}

export function OverviewPageSkeleton(): ReactNode {
  return (
    <Busy label="Loading overview">
      <div className="flex flex-col gap-12">
        <PageSection headingId="current-status-heading" title="Current status">
          <StatusListSkeleton />
        </PageSection>
        <PageSection headingId="history-heading" title="History">
          <TabChrome labels={['24 hours', '7 days']} />
          <div className="pt-5">
            <StatusHistorySkeleton />
          </div>
        </PageSection>
      </div>
    </Busy>
  );
}

export function MemoryPageSkeleton(): ReactNode {
  return (
    <Busy label="Loading memory health">
      <div className="flex flex-col gap-12">
        <PageSection headingId="assessment-heading" title="Assessment">
          <div className="space-y-2" aria-hidden="true">
            <Skeleton className="h-4 w-3/4 max-w-md" />
            <Skeleton className="h-4 w-1/2 max-w-sm" />
          </div>
          <MetricGridSkeleton count={4} />
        </PageSection>
        <PageSection headingId="memory-details-heading" title="Details">
          <TabChrome
            labels={['Archive', 'Derivation', 'Notes & jobs', 'Recovery', 'Cost & voice']}
          />
          <div className="pt-5">
            <MetricGridSkeleton count={6} />
          </div>
        </PageSection>
        <PageSection headingId="channels-heading" title="Channels">
          <TableSkeleton columns={5} rows={8} />
        </PageSection>
      </div>
    </Busy>
  );
}

export function ChannelTableSkeleton(): ReactNode {
  return (
    <Busy label="Loading channels">
      <TableSkeleton columns={5} rows={8} />
    </Busy>
  );
}

export function OperationsPageSkeleton(): ReactNode {
  return (
    <Busy label="Loading operations">
      <div className="flex flex-col gap-12">
        <PageSection headingId="controls-heading" title="Controls">
          <div className="flex flex-wrap gap-2" aria-hidden="true">
            <Skeleton className="h-7 w-44" />
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-7 w-52" />
          </div>
        </PageSection>
        <PageSection headingId="queue-heading" title="Queue">
          <MetricGridSkeleton count={6} className="sm:grid-cols-3 xl:grid-cols-6" />
        </PageSection>
        <PageSection headingId="activity-heading" title="Activity">
          <TabChrome labels={['Runs', 'Job failures', 'Cursor failures']} />
          <div className="pt-5">
            <TableSkeleton columns={7} rows={7} />
          </div>
        </PageSection>
      </div>
    </Busy>
  );
}

export function QueryLabPageSkeleton(): ReactNode {
  return (
    <Busy label="Loading Query Lab">
      <PageSection headingId="query-form-heading" title="Run a query as a member">
        <div className="flex flex-col gap-4" aria-hidden="true">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldSkeleton controlClassName="h-7 w-full" />
            <FieldSkeleton controlClassName="h-7 w-full" />
            <FieldSkeleton controlClassName="h-7 w-full" />
            <FieldSkeleton controlClassName="h-7 w-full" />
          </div>
          <FieldSkeleton controlClassName="h-20 w-full" />
          <Skeleton className="h-7 w-24" />
        </div>
      </PageSection>
    </Busy>
  );
}

export function QueryResultsSkeleton(): ReactNode {
  return (
    <Busy label="Running the diagnostic query">
      <PageSection headingId="query-result-heading" title="Result">
        <TabChrome labels={['Evidence', 'Run details']} />
        <div className="pt-5">
          <TableSkeleton columns={6} rows={5} />
        </div>
      </PageSection>
    </Busy>
  );
}

export function ServerSettingsSkeleton(): ReactNode {
  return (
    <Busy label="Loading server settings">
      <SettingsPage>
        <SettingsSection title="Memory catch-up">
          <SettingsRowSkeleton control="switch" />
        </SettingsSection>
        <SettingsSection title="Hands-free voice">
          <SettingsRowSkeleton control="text" />
          <SettingsRowSkeleton control="select" />
        </SettingsSection>
      </SettingsPage>
    </Busy>
  );
}

export function BotSettingsSkeleton(): ReactNode {
  return (
    <Busy label="Loading bot settings">
      <SettingsPage>
        <SettingsSection title="Models and memory">
          {times(5).map((index) => (
            <SettingsRowSkeleton key={index} control="text" />
          ))}
        </SettingsSection>
        <SettingsSection title="Cost and voice">
          {times(6).map((index) => (
            <SettingsRowSkeleton key={index} control="text" />
          ))}
        </SettingsSection>
        <SettingsSection title="Operations">
          {times(6).map((index) => (
            <SettingsRowSkeleton key={index} control="text" />
          ))}
        </SettingsSection>
      </SettingsPage>
    </Busy>
  );
}

export function SessionSkeleton(): ReactNode {
  return (
    <main className="grid min-h-dvh place-items-center bg-bg-shell p-4">
      <Busy label="Restoring your session">
        <div className="w-full max-w-[17rem]">
          <Skeleton className="h-7 w-full" />
        </div>
      </Busy>
    </main>
  );
}

export function ViewSkeleton({ path }: { path: RoutePath }): ReactNode {
  switch (path) {
    case '/':
      return <OverviewPageSkeleton />;
    case '/memory':
      return <MemoryPageSkeleton />;
    case '/query-lab':
      return <QueryLabPageSkeleton />;
    case '/operations':
      return <OperationsPageSkeleton />;
    case '/server-settings':
      return <ServerSettingsSkeleton />;
    case '/settings':
      return <BotSettingsSkeleton />;
    default: {
      const _exhaustive: never = path;
      return _exhaustive;
    }
  }
}
